/** Plain-English names and groups for the alert catalogue (packages/shared/src/alerts.ts). Unknown keys fall back to a readable form. */

export const ALERT_GROUPS = ['Approvals and risk', 'Trades and reports', 'Daily analysis', 'Setups', 'Handover', 'Floor and discussion', 'Enrolment and admin', 'Other'] as const;
type Group = (typeof ALERT_GROUPS)[number];

const LABELS: Record<string, [string, Group]> = {
  'plan.pending_approval': ['A plan needs my approval', 'Approvals and risk'],
  'plan.decided': ['My plan was approved or rejected', 'Approvals and risk'],
  'plan.approval_expiring': ['An approval is about to expire', 'Approvals and risk'],
  'trade.stop_widened': ['A stop loss was widened', 'Approvals and risk'],
  'risk.daily_loss_warning': ['Daily loss warning (80 %)', 'Approvals and risk'],
  'risk.daily_loss_hit': ['Daily loss limit reached', 'Approvals and risk'],
  'progress.due': ['Progress report due', 'Trades and reports'],
  'progress.overdue': ['Progress report overdue', 'Trades and reports'],
  'progress.missed': ['Progress report missed', 'Trades and reports'],
  'closing_report.due': ['Closing report due', 'Trades and reports'],
  'closing_report.missed': ['Closing report missed', 'Trades and reports'],
  'trade.opened': ['A trade was opened', 'Trades and reports'],
  'trade.uncovered_quiet_hours': ['Open trade without cover in quiet hours', 'Trades and reports'],
  'analysis.due': ['Daily analysis due', 'Daily analysis'],
  'analysis.missed': ['Daily analysis missed', 'Daily analysis'],
  'analysis.published': ['An analysis was published', 'Daily analysis'],
  'analysis.review_needed': ['An analysis needs review', 'Daily analysis'],
  'setup.update_due': ['Setup update due', 'Setups'],
  'setup.stale': ['A setup went stale', 'Setups'],
  'setup.status_changed': ['A followed setup changed status', 'Setups'],
  'setup.expired': ['My setup expired', 'Setups'],
  'handover.requested': ['Someone asked me to cover', 'Handover'],
  'handover.not_accepted': ['My handover wasn’t accepted', 'Handover'],
  'handover.started': ['A handover started', 'Handover'],
  'handover.ended': ['A handover ended', 'Handover'],
  'enrollment.received': ['New application received', 'Enrolment and admin'],
  'enrollment.decided': ['Application decided', 'Enrolment and admin'],
  'policy.updated': ['Floor rules or terms were updated', 'Enrolment and admin'],
  'symbol.requested': ['A trader asked to cover a symbol', 'Enrolment and admin'],
  'symbol.request_decided': ['My symbol request was decided', 'Enrolment and admin'],
  'member.suspended': ['A trader with open trades was suspended', 'Approvals and risk'],
  'signal.received': ['A TradingView signal on one of my symbols', 'Setups'],
  'price.fetch_failed': ['Delayed prices could not be updated', 'Enrolment and admin'],
  'fx.fetch_failed': ['FX rates could not be updated', 'Enrolment and admin'],
  'reminder.fired': ['My reminders', 'Other'],
  'webinar.reminder': ['Webinar reminder', 'Other'],
  'webinar.scheduled': ['A webinar was scheduled for me', 'Floor and discussion'],
  'webinar.changed': ['A webinar was moved or cancelled', 'Floor and discussion'],
  'webinar.live': ['A webinar I can join is live now', 'Floor and discussion'],
  'room.invited': ['I was invited to a live room', 'Floor and discussion'],
  'stream.destination_failed': ['A streaming destination of my broadcast failed', 'Floor and discussion'],
  'recording.pending_approval': ['A recording is waiting for YouTube approval', 'Floor and discussion'],
  'recording.changes_requested': ['Changes requested on my recording’s YouTube package', 'Floor and discussion'],
  'recording.published': ['A recording was published to YouTube', 'Floor and discussion'],
  'recording.publish_failed': ['Publishing a recording to YouTube failed', 'Floor and discussion'],
};

export function alertLabel(key: string): { label: string; group: Group } {
  const known = LABELS[key];
  if (known) return { label: known[0], group: known[1] };
  const [area = '', ...rest] = key.split('.');
  const label = `${area.charAt(0).toUpperCase()}${area.slice(1)}: ${rest.join(' ').replace(/_/g, ' ')}`;
  const group: Group = /comment|mention|follow|reaction/.test(key) ? 'Floor and discussion' : /setup/.test(key) ? 'Setups' : /handover/.test(key) ? 'Handover' : /enrol|policy|symbol/.test(key) ? 'Enrolment and admin' : 'Other';
  return { label, group };
}

export const CHANNEL_LABEL: Record<string, string> = { IN_APP: 'In-app', WEB_PUSH: 'Browser', TELEGRAM: 'Telegram', EMAIL: 'Email' };
